import { Component, EventEmitter, Input, OnInit, Output, Type } from '@angular/core';
import { catchError, filter, switchMap, tap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { EMPTY } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { StringUtils } from '../../common/string-utils';
import { ActionService } from './action.service';
import { ActionModalComponent } from './action-modal/action-modal.component';
import { ActionConfigDTO, RefDTO } from '../../system/system-dtos';
import { ComponentType } from '@angular/cdk/portal';
import { isDefined } from '../utils/utils';
import { ActionModalRegistryService } from './action-modal-registry.service';

@Component({
  selector: 'app-action',
  templateUrl: './action.component.html',
  styleUrls: ['./action.component.scss'],
  standalone: false,
})
export class ActionComponent implements OnInit {
  @Input() actionConfig: ActionConfigDTO;

  @Input() data: any;

  @Input() menuItemMode = false;

  @Input() simple = false; // if that is set to true we'll show only label

  @Output() onActionStart = new EventEmitter<void>();

  @Output() onResponse = new EventEmitter<any>();

  public loading = false;

  isDisabled = true;

  hasWarning = false;

  nestedMenus: any;

  public isDisabledChildren = {};

  public isChildWithWarning = {};

  anyChildrenAvailable: any;

  uploadFilename: string;

  constructor(
    private dialog: MatDialog,
    private http: HttpClient,
    private route: ActivatedRoute,
    private toastrService: ToastrService,
    private actionService: ActionService,
    private router: Router,
    private actionModalRegistry: ActionModalRegistryService
  ) {}

  public determineIsDisabled(data: any, editCondition: string) {
    return this.actionService.determineIsDisabled(data, editCondition);
  }

  ngOnInit() {
    this.isDisabled = this.determineIsDisabled(this.data, this.actionConfig.jsEditCondition);
    this.hasWarning = this.actionService.determineHasWarning(this.data, this.actionConfig.jsWarningCondition);
    this.isDisabledChildren = {};
    if (this.actionConfig.children && this.actionConfig.children.length > 0) {
      this.actionConfig.children.forEach((a) => {
        this.isDisabledChildren[a.buttonLabel] = this.determineIsDisabled(this.data, a.jsEditCondition);
        this.isChildWithWarning[a.buttonLabel] = this.actionService.determineHasWarning(
          this.data,
          a.jsWarningCondition
        );
      });
    }
    this.anyChildrenAvailable =
      this.actionConfig.children &&
      this.actionConfig.children.length > 0 &&
      Object.keys(this.isDisabledChildren).find((key) => !this.isDisabledChildren[key]);
  }

  public onClick(action: ActionConfigDTO) {
    if (!action.url && action.ref) {
      const url = this.toUrl(action.ref, this.data);
      this.router.navigate([url], { relativeTo: this.route.parent });
      return;
    }

    let dialogModalType: ComponentType<any> = ActionModalComponent;
    if (isDefined(action.customModal) && isDefined(this.actionModalRegistry.getActionModalByName(action.customModal))) {
      dialogModalType = this.actionModalRegistry.getActionModalByName(action.customModal);
    }

    const dialogRef = this.dialog.open(dialogModalType, {
      minHeight: action.minModalHeight,
      minWidth: action.minModalWidth,
      data: {
        config: action,
        entity: this.data,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(filter((d) => !!d))
      .pipe(
        switchMap((dialogReturn) => {
          this.loading = true;
          this.onActionStart.emit();

          const { params } = this.route.snapshot;
          if (params.entityId && !this.data.id) {
            this.data.id = params.entityId;
          }

          const targetUrl = StringUtils.convert(action.url, this.data);

          return ActionService.actionRequest(
            this.route.snapshot.params,
            this.data.id,
            action,
            dialogReturn,
            targetUrl,
            this.http,
            this.toastrService
          );
        })
      )
      .pipe(
        catchError((e) => {
          this.loading = false;
          this.onResponse.emit(e);
          return EMPTY;
        })
      )
      .subscribe((response: HttpResponse<any>) => {
        if (action.downloadAction) {
          const contentTypeHeader = response.headers.get('Content-Type');
          const fileName = this.getFileName(response);
          const blob = new Blob([response.body as Blob], { type: contentTypeHeader });
          const url = URL.createObjectURL(blob);

          const link = document.createElement('a');
          link.href = url;

          link.setAttribute('download', fileName);
          link.style.display = 'none';

          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          this.onResponse.emit('');
          this.toastrService.success(action.successMessage);
        } else if (action.ref) {
          if (action.ref) {
            const url = this.toUrl(action.ref, response);
            this.router.navigate([url], { relativeTo: this.route.parent });
          }
        } else {
          this.loading = false;
          this.onResponse.emit(response);
          this.toastrService.success(action.successMessage);
        }
      });
  }

  hashCode(str: string): number {
    return str.split('').reduce((prevHash, currVal) => ((prevHash << 5) - prevHash + currVal.charCodeAt(0)) | 0, 0);
  }

  private getFileName(response: HttpResponse<any>) {
    const contentDispositionHeader = response.headers.get('Content-Disposition');
    if (contentDispositionHeader) {
      const result = contentDispositionHeader.split(';')[1].trim().split('=')[1];
      return result.replace(/"/g, '');
    }
  }

  private toUrl(ref: RefDTO, data: any): string {
    let url = `./${ref.entity}`;
    if (ref.code) {
      url += `/${StringUtils.convert(ref.code, data)}`;
    }
    if (ref.tab) {
      url += `/${ref.tab}`;
    }
    if (ref.nestedId) {
      url += `/${StringUtils.convert(ref.nestedId, data)}`;
    }

    return url;
  }
}
